import React, { useState, useEffect, useRef } from "react";
import useFilters from "hooks/useFilters";
import FileSaver from "file-saver";

import { useHistory } from "react-router-dom";
import { useStore } from "../../../../store/context";
import i18n from "i18n";

import { OrderService } from "../../../../services/OrderService";
import { PublicService } from "../../../../services/PublicService";
import { formatDateToYyyyMmDd } from "helpers/Parsers";

import AdminLayout from "../../../../components/MainApp/layouts/DesktopLayout";
import MainNotification from "../../../../components/MainApp/atoms/Notification";
import FilterPanel from "../../../../components/MainApp/organisms/Filters/FilterPanel";
import OrdersTable from "../../../../components/MainApp/organisms/Orders/PrepOrders/Table";
import Pagination from "../../../../components/MainApp/atoms/Pagination";
import Loader from "../../../../components/MainApp/atoms/Loader";
import Search from "../../../../components/MainApp/atoms/Filters/Search";

import tableHeaders from "./data/tableHeaders";
import {
  filterData as FilterDataSource,
  filterTypeMap
} from "./data/filterData";
import recTypeMapping from "./data/RecTypeMapping";

import { isMobile } from "../../../../helpers/Mobile";
import styles from "./RecOrders.module.scss";
import excel from "assets/images/excel.svg";
import excelwhite from "assets/images/excelwhite.svg";
import SimpleSearchPanel from "components/MainApp/organisms/Filters/SimpleSearchPanel";
import { NotFoundItems } from "views/Admin/components/NotFoundItems";
import { capitalize } from "helpers/utils/utils_general";

const initialFilters = {
  page_size: 10,
  search: "",
  ordering: "-created_at",
  status: undefined,
  node: undefined,
  destination: undefined,
  date: [null, null],
};

let scrolling = false;

export const RecOrdersView = () => {
  const [recOrderFilters, setRecOrderFilters] = useState();
  const history = useHistory();
  const [loading, setloading] = useState(false);
  const [noItems, setNoItems] = useState(false);
  const [filterData, setFilterData] = useState(FilterDataSource);

  const [orders_name, setOrders_name] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const myRef = useRef(0);

  const [
    filters,
    setFilters,
    selectedFiltersAll,
    selectedFiltersByType,
    handleApplyFilters,
    handleSearch,
    headerOrderingHandler,
    resetOrdering,
    handleFiltersChangeOrdering,
    applied,
    page,
    setPage,
    items,
    setItems,
    pager,
    setPager,
    header,
    setHeader,
    mobilePanelOpen,
    setMobilePanelOpen
  ] = useFilters(
    initialFilters,
    recOrderFilters,
    filterTypeMap,
    isMobile,
    tableHeaders
  );

  const headerOptions = [
    {
      icon: "icon-iconos_mas2",
      name: "add",
      handler: () => {
        history.push("/admin/orders/reception/load");
      },
      tooltip: i18n.t("create.order")
    }
  ];

  useEffect(() => {
    /**
     * Initial component mount:
     * - Configures filter data structure.
     * - Fetches order codes (REC type) for the search autocomplete suggestions.
     */
    fillFilterData();
    OrderService.ordersCode({ "type": "REC" }).then(response => {
      setOrders_name(response);
    });
  }, []);

  useEffect(() => {
    setMobilePanelOpen(false);

    // Check if the current filter state corresponds to the initial configuration
    const first_load =
      JSON.stringify(initialFilters) === JSON.stringify(filters);
    filter(first_load);

    /**
     * UI Synchronization:
     * Synchronizes table header indicators (active/direction) with the current 
     * ordering state from the useFilters hook.
     */
    const keyOrdering =
      filters.ordering.charAt(0) === "-"
        ? filters.ordering.substr(1)
        : filters.ordering;

    if (keyOrdering in tableHeaders) {
      const newHeaders = { ...header };
      newHeaders[keyOrdering]["active"] = true;
      newHeaders[keyOrdering]["direction"] =
        filters.ordering === keyOrdering ? true : false;
      setHeader(newHeaders);
    }
  }, []);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    filter(false);
    setRecOrderFilters({
      filters: filters,
      selectedFiltersAll: selectedFiltersAll,
      selectedFiltersByType: selectedFiltersByType
    });
  }, [filters, page]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  });

  const handleScroll = event => {
    if (!isMobile) return;

    if (
      event.srcElement.offsetHeight + event.target.scrollTop + 100 <
      event.srcElement.scrollHeight
    )
      return;

    if (!pager.next || submitted || scrolling) return;
    scrolling = true;

    setPage(page + 1);
  };

  const handlePagerChange = function (page) {
    setPage(page);
  };

  const filter = function (firstLoad = false) {
    let append = isMobile && page > 1;
    if (!append) {
      setItems([]);
    }

    //BEFORE SUBMIT
    document.body.style.cursor = "wait";
    setSubmitted(true);
    // setErrors({})
    // setMessage({})

    const data = { ...filters, page: page };
    data["status"] = data["status"] ? data["status"].join(",") : undefined;
    data["node"] = data["node"] ? data["node"].join(",") : undefined;
    data["destination"] = data["destination"]
      ? data["destination"].join(",")
      : undefined;

    /**
     * SAFE DATE FORMATTING:
     * Formats date objects into YYYY-MM-DD strings for backend integration.
     */
    if (data["date"] && Array.isArray(data["date"]) && data["date"][0]) {
      data["from_date"] = formatDateToYyyyMmDd(data["date"][0]);
      data["from_date_to"] = data["date"][1] ? formatDateToYyyyMmDd(data["date"][1]) : undefined;
    }

    delete data["date"];

    setloading(true);
    OrderService.recOrders(data)
      .then(response => {
        //this.afterSubmit();
        setSubmitted(false);
        document.body.style.cursor = "default";
        scrolling = false;

        // if (firstLoad && response.count === 0) {
        //   history.push("/admin/products/load");
        //   setNoProducts(true)
        //   return;
        // }

        const newOrders = append
          ? [...items, ...response.results]
          : response.results;

        setloading(false);
        setItems(newOrders);
        setPager({
          next: response.next,
          previous: response.previous,
          count: response.count,
          countPages: Math.ceil(response.count / filters.page_size)
        });
      })
      .catch(e => {
        setSubmitted(false);
        document.body.style.cursor = "default";
        scrolling = false;
      });
  };

  /**
   * Component Mounting Hook:
   * 1. Prepares the internal filter configuration (labels, types, and mobile sorting).
   * 2. Fetches specific order codes (REC) for autocomplete functionality.
   * * NOTE: PublicService.nodes() call was removed for the Reception view because
   * filtering by Node is not required here. 
   * This optimizes initial load by avoiding an unnecessary API request.
   */
  const fillFilterData = function () {
    setFilterData(() => {
      /**
       * Map base filters and ensure they are enabled. 
       * Reconstructs objects to maintain immutable reference integrity.
       */
      let baseFilters = FilterDataSource.map(f => ({ ...f, enabled: true }));

      if (isMobile) {
        /**
         * Mobile Sorting Injection:
         * Dynamically adds the 'ordering' filter to the mobile panel 
         * if it doesn't already exist.
         */
        const hasOrdering = baseFilters.some(f => f.name === "ordering");
        if (!hasOrdering) {
          baseFilters.push({
            name: "ordering",
            type: "twoOption",
            options: [
              { selected: false, label: i18n.t("less.recent"), value: "created_at" }
            ],
            label: i18n.t("order.by"),
            firstOptionLabel: i18n.t("latest"),
            value: "-created_at",
            enabled: true
          });
        }
      }
      return baseFilters;
    });
  };

  /**
   * Component Mounting Hook:
   * 1. Prepares the internal filter configuration (labels, types, and mobile sorting).
   * 2. Fetches specific order codes (REC) for autocomplete functionality.
   * Note: PublicService.nodes() call was removed in favor of static 
   * or centralized filter data for this specific view.
   */
  useEffect(() => {
    fillFilterData();

    OrderService.ordersCode({ "type": "REC" }).then(response => {
      setOrders_name(response);
    });
  }, []); // Execute strictly on initial mount

  const downloadReport = () => {
    const data = { ...filters };
    data["status"] = data["status"] ? data["status"].join(",") : undefined;
    data["node"] = data["node"] ? data["node"].join(",") : undefined;
    data["destination"] = data["destination"]
      ? data["destination"].join(",")
      : undefined;

    setloading(true);
    OrderService.receptionDownloadReport(data)
      .then(blob => {
        setloading(false);
        FileSaver.saveAs(blob, "reporte.xls");
      })
      .catch(error => {
        setloading(false);
      });
  };

  const goToDetailPage = event => {
    const item = items.find(
      o => o.id === event.currentTarget.dataset.requestorderId
    );
    // if (item.klass === "REQ" || ["COM", "REJ"].includes(item.status)) {
    history.push(`/admin/orders/reception/details/${item.id}`);
    // } else {
    //   history.push(`/admin/orders/reception/edit/${item.id}`);
    // }
  };

  const goToEditPage = event => {
    history.push(
      `/admin/orders/reception/details/${event.currentTarget.dataset.requestorderId}`
    );
  };

  const headerButtons = (
    <div
      className="excel-button"
      onClick={downloadReport}
      style={{
        width: "30px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
      }}
    >
      <img style={{ height: "30px" }} src={isMobile ? excelwhite : excel} />{" "}
    </div>
  );

  const mobileContent = (
    <React.Fragment>
      <div className={styles["orders-request-table-filter-container"]}>
        <SimpleSearchPanel
          placeholder={i18n.t("search.by.name.or.code")}
          handleSubmit={handleSearch}
        />
      </div>

      <div
        className={`${styles["orders-request-table-mobile-panel"]} ${
          mobilePanelOpen ? styles["opened"] : ""
        }`}
      >
        <FilterPanel
          resultList={orders_name}
          placeholder={i18n.t("search.by.name.or.code")}
          searchValue={filters.search}
          handleSubmitSearch={handleSearch}
          filters={filterData}
          selectedFiltersAll={selectedFiltersAll}
          selectedFiltersByType={selectedFiltersByType}
          handleApplyFilters={handleApplyFilters}
          closeMobilePanel={() => {
            setMobilePanelOpen(false);
          }}
        ></FilterPanel>
      </div>
      <div className={styles["orders-request-table-table"]}>
        <div className={styles["orders-request-table-top-table"]}>
          <div className={styles["orders-request-table-top-total"]}>
            {capitalize(i18n.t("total"))}{" "}
            <span className={styles["orders-request-table-top-total-bold"]}>
              ({pager.count})
            </span>
          </div>
          <div
            className={`${styles["orders-request-table-top-sort"]} ${
              applied ? styles["orders-request-table-active-filter"] : ""
            }`}
            onClick={() => {
              setMobilePanelOpen(true);
            }}
          >
            <i
              className={`icon-iconos_filtros ${styles["orders-request-table-top-icon"]}`}
            ></i>
          </div>
          
        </div>
        <OrdersTable
            requestOrders={items}
            headers={header}
            handleClickRequestOrder={goToDetailPage}
            mapTypes={recTypeMapping}
          />
      </div>

      {items.length === 0 && !submitted && <NotFoundItems />}
    </React.Fragment>
  );

  // DESKTOP START
  const desktopContent = (
    <div className={styles["orders-request-content-wrapper"]}>
      <FilterPanel
        resultList={orders_name}
        placeholder={i18n.t("search.by.name.or.code")}
        searchValue={filters.search}
        handleSubmitSearch={handleSearch}
        filters={filterData}
        selectedFiltersAll={selectedFiltersAll}
        selectedFiltersByType={selectedFiltersByType}
        handleApplyFilters={handleApplyFilters}
      ></FilterPanel>

      {items.length > 0 && (
        <div className={styles["orders-request-table-table"]}>
          <div className={styles["orders-request-table-top-table"]}>
            <div className={styles["orders-request-table-top-total"]}>
              {i18n.t("total").toUpperCase()}{" "}
              <span className={styles["orders-request-table-top-total-bold"]}>
                ({pager.count})
              </span>
            </div>
            <div className={styles["orders-request-table-top-sort"]}>
              <div
                data-automation-id="orders-request-table-sort"
                className={
                  filters.ordering.includes("created_at")
                    ? `${styles["orders-request-table-top-sort"]} ${styles["orders-request-table-active-filter"]}`
                    : styles["orders-request-table-top-sort"]
                }
                onClick={() => {
                  handleFiltersChangeOrdering(
                    "ordering",
                    filters.ordering === "-created_at"
                      ? "created_at"
                      : "-created_at"
                  );
                  resetOrdering();
                }}
              >
                {filters.ordering === "created_at" ? (
                  <React.Fragment>
                    {i18n.t("less.recent")}{" "}
                    <i className={`icon-chevron ${styles["orders-request-table-top-icon"]}`}></i>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {" "}
                    {i18n.t("latest")}{" "}
                    <i className={`icon-chevron-down ${styles["orders-request-table-chevron-down"]} ${styles["orders-request-table-top-icon"]}`}></i>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <OrdersTable
            requestOrders={items}
            headers={tableHeaders}
            headerClickHandler={headerOrderingHandler}
            handleClickRequestOrder={goToDetailPage}
            handleClickDraft={goToEditPage}
            mapTypes={recTypeMapping}
          />
        </div>
      )}

      {items.length === 0 && !submitted && (
        <NotFoundItems />
      )}
      {items.length > 0 && (
        <div>
          <Pagination
            pageQuantity={pager.countPages}
            currentPage={page}
            pageChangeHandler={handlePagerChange}
          />
        </div>
      )}
    </div>
  );
  // DESKTOP END

  const content = (
    <div className={styles["orders-request-content-container"]}>
      {isMobile ? mobileContent : desktopContent}
    </div>
  );

  return (
    <div className={styles["orders-request-container"]}>
      <AdminLayout
        headerTitle={i18n.t("entry.orders")}
        headerOptions={headerOptions}
        content={content}
        navHidden={false}
        customButtons={headerButtons}
        goBackFunc={() => {
          history.push("/admin/orders");
        }}
      ></AdminLayout>
      {loading && <Loader />}
      <MainNotification
        label={i18n.t("product.updated.successfully.message")}
        open={false}
      ></MainNotification>
    </div>
  );
};

export default RecOrdersView;
